import { Typography } from '@mui/material';
import { styled } from '@mui/system';
import * as astCtx from '../../elf-core/ElfDefaultAstContext';

const StyledTypography = styled(Typography)(() => ({
	fontFamily: '\'Press Start 2P\'',
}));

const ElfDocs = () => {
	const { meta, keywords, instructions, primitives } = astCtx.build({ maxNumberOfRegisters: 8 }).languageContext;

	return (
		<div style={{ padding: 16, fontFamily: '\'Press Start 2P\'' }}>
			<StyledTypography variant="h3" gutterBottom>Jezik ELF</StyledTypography>
			<hr></hr>
			<p>
				Nekoč, daleč na severu, v kleti Božičkovega stanovanja (takrat še Božičkov laboratorij za napredno znanost ni obstajal), so pionirski znanstveniki-palčki ustvarili nekaj, čemur so ponosno rekli <em>Eksperimentalni Low-level Format</em>. Zvenelo je pametno, resno in zelo tehnično - dokler niso ugotovili, da stvar... ne dela.
			</p>

			<p>
				Tako se je začelo mučno obdobje jezika, kjer je začasno prejel nov akronim: <em>Eno Leto Flikamo</em>.
				Dvanajst mesecev, 365 dni (in še nekaj neprespanih noči), so palčki krpali in pilili kodo ELF - včasih iz navdiha, drugič iz čistega obupa.
				Da bi zadeva sploh delovala, so celo razvili poseben <strong>ELF-procesor</strong>,
				ki je zaradi svoje občutljive polarne tehnologije sposoben delovati samo na polarnem kontinentu.
			</p>

			<p>
				A iz vseh teh muk in uničenih tipkovnic se je rodil pravi čudež - <em>ELF-script</em>.
				Zdaj deluje, kot mora (večinoma), in ne potrebuje več toliko obližev.
				Postal je zanesljivo orodje, ki natančno, ponovljivo in pošteno oceni algoritmično rešitev in uporabnikove računalniške živce.
			</p>

			<h2>ELF Studio</h2>

			<p>Jezik lahko preizkusiš tukaj v razvijalnem okolju <a href="/elf-studio" target="_blank">[ELF-Studio]</a>!
				Orodje je še v povojih in deluje večino časa, le tu in tam gre lahko kaj narobe. Statistično gledano je torej kar zanesljivo.</p>

			<h3>Namigi za urejevalnik</h3>
			<ul style={{ listStyleType: 'none' }}>
				<li>* <code>CTRL/CMD + P</code> - skoči na določeno vrstico/stolpec (vneseš :vrstica:stolpec ali :vrstica in skočiš na to mesto).</li>
				<li>* <code>CTRL/CMD + K</code> - zakomentira ali odkomentira izbrane vrstice / mesto kurzorja.</li>
				<li>* <code>CTRL/CMD + SPACE</code> - pokaže namige, ki jih lahko preletiš s smernimi puščicami.</li>
				<li>* <code>CTRL/CMD + S</code> - izvozi .elf kodo v tekstovno datoteko.</li>
				<li>* <code>F4</code> - zažene program v produkcijskem načinu</li>
				<li>* <code>F5</code> - zažene ali nadaljuje program v razhroščevalnem načinu, do naslednje točke prekinitve.</li>
				<li>* <code>SHIFT + F5</code> - ustavi razhroščevanje in ponastavi program.</li>
				<li>* <code>F9</code> - nastavi ali odstrani razhroščevalno točko (breakpoint) na trenutni vrstici.</li>
				<li>* <code>F10</code> - izvede naslednjo inštrukcijo (step over).</li>
				<li>* <code>F11</code> - izvedi korak inštrukcije / vstopi v funkcijo (step into).</li>
				<li>* Miško premakni na inštrukcijo za prikaz natančnih informacij o njej.</li>
				<li>* Razhroščevalni način omogoča natančen pregled stanja programa ob vsakem koraku izvajanja:
					<ul style={{ listStyleType: 'none' }}>
						<li>* Razhroščevalne točke (breakpoints) lahko nastaviš s klikom levo ali desno od številke vrstice.</li>
						<li>* Rdeča plast barve nad ukazom pri razhroščevanju ponazarja stanje pred izvedbo, zelena plast barve pa stanje po izvedbi ukaza</li>
						<li>* <span style={{ color: 'gold' }}>Med razhroščevanjem imaš dostop do kopije stanja programa, zato lahko v terminal takrat pišeš inštrukcije</span> (ali celo programe - eno vrstične), da poizveš kaj več o stanju programa v dani točki (npr. PRINT @1). Po evalvaciji se bo stanje programa povrnilo na stanje pred njo - zato lahko preizkušaš različne programe, brez da bi vplival na potek programa, ki ga razhroščuješ</li>
					</ul>
				</li>
				<li>* <span style={{ color: 'gold' }}>Kadar ne razhroščuješ lahko tudi v terminal pišeš začasne ukaze / programe - &quot;one-linerje&quot;</span>, da preveriš delovanje. Pred vsakim zagonom se predpostavlja, da je program zagnan znova (ne ohrani se spomin)</li>
			</ul>

			<h2>Dokumentacija <i style={{ fontSize: 10 }}>(za piflarje)</i></h2>
			<p>Za tiste (večino), ki se jim pa ne da brati dokumentacije, je pripravljen tudi kratek povzetek kar v kodi, ki ga najdeš <a href="/elf-studio-tutorial" target="_blank">[tukaj]</a>.</p>
			<h3>Omejitve znakov</h3>
			<p>
				Izvorna koda ELF Script lahko vsebuje le ASCII znake. Izjema so le komentarji, ki lahko vsebujejo tudi poljubne znake oz. ne-ASCII simbole.
			</p>
			<h3>Komentarji</h3>
			<p>
				Celo-vrstične komentarje začneš z znakom <code>~</code> na začetku vrstice.
				Več-vrstične komentarje <code>( ... več vrstični komentar ... )</code> lahko postaviš kjerkoli v kodi; če vsebujejo zaklepaje, jih je potrebno ubežati z <code>\</code>.
				Komentarji podpirajo tudi ne-ASCII znake, npr. 😎.
			</p>

			<h3>Ločila</h3>
			<p>
				Ločila so prazni znaki: presledek, tab ali nova vrstica in določajo meje med elementi kode.
				Odločiš se sam, ali boš pisal čitljivo kodo ali pa abstraktno umetnost.
			</p>

			<h3>Števila in operacije</h3>
			<p>
				Vse številke so v 64-bitnem IEEE 754 formatu z dvojno natančnostjo.
				Pri bitnih operacijah se uporabljajo 32-bitne vrednosti, a se rezultat zapiše nazaj v 64-bitni format.
				Števila lahko zapišeš kot cela ali decimalna (<code>1</code>, <code>1.234</code>, <code>+1</code>, <code>+1.234</code>, <code>-1</code>, <code>-1.234</code>,...) ali kot ASCII vrednosti, npr. <code>#`A`</code>.
			</p>

			<h3>Pomnilnik</h3>
			<p>
				Vse celice pomnilnika so prvotno (razen če je v nalogi specificirano drugače) nastavljene na 0.
				Pri dostopu do pomnilnika se upošteva le celi del števila; decimalni del se ignorira.
			</p>

			<h3>Registri</h3>
			<p>
				Vsi registri so prvotno (razen če je v nalogi specificirano drugače) nastavljeni na 0.
			</p>

			<h3>Cikli procesorja in omejitve</h3>
			<p>
				Vsaka rezervirana beseda, inštrukcija ali dostop do podatkov porabi nekaj CPU ciklov.
				Izvajalno okolje lahko omeji število registrov, velikost sklada (stack), velikost pomnilnika (heap), globino rekurzije in razpoložljive CPU cikle.
				Te omejitve se nastavijo preko URL vrstice brskalnika v ELF Studiu.
			</p>

			<h3>Konstrukti jezika</h3>
			{/* PRIMITIVES */}
			<ul style={{ fontSize: 18, listStyleType: 'none' }}>
				<li>
					<em>Osnovni izrazi</em>
					<p>Osnovni izrazi so najmanjši in najpomembnejši gradniki jezika, ki predstavljajo temeljne enote za delo s podatki in ukazi. Predstavljajo točke, kjer program komunicira s podatki kot so števila, konstante, registri ali pomnilnik.</p>
					<li><br /></li>
					<ul style={{ listStyleType: 'none' }}>
						{Object.entries(primitives).map(([key, prim]) => (
							<li key={key} style={{ marginBottom: 12 }}>
								<b><span style={{ color: 'gold' }}>* {prim.metadata.name}</span></b>
								<ul style={{ listStyleType: 'none' }}>
									<li><span style={{ color: 'gray' }}>Opis:</span> {prim.metadata.description}</li>
									<li><span style={{ color: 'gray' }}>Poraba elf cpu:</span> {prim.metadata.cyclesDescription}</li>
									<li><span style={{ color: 'gray' }}>Primer zapisa:</span> {prim.metadata.example}</li>
								</ul>
							</li>
						))}
					</ul>
				</li>
			</ul>

			{/* META */}
			<ul style={{ fontSize: 18, listStyleType: 'none' }}>
				<li>
					<em>Napredni izrazi kot argumenti</em>
					<p>Določeni argumenti rezerviranih inštrukcij podpirajo posebne strukture, ki poenostavijo in optimizirajo izvajanje ukazov ter prenos večjih količin podatkov.</p>
					<li><br /></li>
					<ul style={{ listStyleType: 'none' }}>
						{Object.entries(meta.batches).map(([key, val]) => (
							<li key={key} style={{ marginBottom: 12 }}>
								<b><span style={{ color: 'gold' }}>* {val.name}</span></b>
								<ul style={{ listStyleType: 'none' }}>
									<li><span style={{ color: 'gray' }}>Opis:</span> {val.description}</li>
									<li><span style={{ color: 'gray' }}>Poraba elf cpu:</span> {val.cyclesDescription}</li>
								</ul>
							</li>
						))}
						<li key="references" style={{ marginBottom: 12 }}>
							<b><span style={{ color: 'gold' }}>* {meta.references.name}</span></b>
							<ul style={{ listStyleType: 'none' }}>
								<li><span style={{ color: 'gray' }}>Opis:</span> {meta.references.description}</li>
								<li><span style={{ color: 'gray' }}>Poraba elf cpu:</span> {meta.references.cyclesDescription}</li>
							</ul>
						</li>
					</ul>
				</li>
			</ul>

			{/* KEYWORDS */}
			<ul style={{ fontSize: 18, listStyleType: 'none' }}>
				<li>
					<em>Ključne besede</em>
					<p>Ključne besede so rezervirani bloki jezika, s pomočjo katerih sestavimo programe in določimo tok izvajanja ukazov.</p>
					<li><br /></li>
					<ul style={{ listStyleType: 'none' }}>
						{Object.entries(keywords).map(([key, kw]) => (
							<li key={key} style={{ marginBottom: 12 }}>
								<b><span style={{ color: 'gold' }}>* {key}</span></b>
								<ul style={{ listStyleType: 'none' }}>
									<li><span style={{ color: 'gray' }}>Opis:</span> {kw.metadata.description}</li>
									<li><span style={{ color: 'gray' }}>Poraba elf cpu:</span> {kw.metadata.cyclesDescription}</li>
									<li><span style={{ color: 'gray' }}>Spreminja akumulator:</span> {kw.metadata.modifiesAccumulator ? 'da' : 'ne'}</li>
									<li><span style={{ color: 'gray' }}>Argumenti:</span> {kw.metadata.arguments.length}</li>

									{kw.metadata.arguments.map((variant: any[], i: number) => (
										<li key={i}>
											<span style={{ color: 'gray' }}>&nbsp;&nbsp;{i + 1}.</span>
											<ul style={{ listStyleType: 'none' }}>
												{variant.map((arg, j) => (
													<li key={j}>
														<ul style={{ listStyleType: 'none' }}>
															<li><span style={{ color: 'gray' }}>Ime: </span>{arg.argName}</li>
															<li><span style={{ color: 'gray' }}>Tip: </span>{arg.argToken}</li>
															<li><span style={{ color: 'gray' }}>Opis: </span>{arg.argDescription}</li>
														</ul>
													</li>
												))}
											</ul>
										</li>
									))}

									<li><span style={{ color: 'gray' }}>Primer zapisa:</span> {kw.metadata.example}</li>
								</ul>
							</li>
						))}
					</ul>
				</li>
			</ul>

			{/* INSTRUCTIONS */}
			<ul style={{ fontSize: 18, listStyleType: 'none' }}>
				<li>
					<em>Inštrukcije</em>

					<p>Medtem ko inštrukcije lahko vsebujejo druge inštrukcije, pa morajo prej omenjene ključne besede stati samostojno v kodi. To pomeni da v ključne besede lahko pošljemo izraze in inštrukcije, v inštrukcije pa ne moremo poslati ključne besede.</p>
					<li><br /></li>
					<ul style={{ listStyleType: 'none' }}>
						{Object.entries(instructions).map(([key, inst]) => (
							<li key={key} style={{ marginBottom: 12 }}>
								<b><span style={{ color: 'gold' }}>* {key}</span></b>
								<ul style={{ listStyleType: 'none' }}>
									<li><span style={{ color: 'gray' }}>Opis:</span> {inst!.metadata.description}</li>
									<li><span style={{ color: 'gray' }}>Poraba elf cpu:</span> {inst!.metadata.cyclesDescription}</li>
									<li>
										<span style={{ color: 'gray' }}>Spreminja akumulator:</span> {inst!.metadata.modifiesAccumulator ? 'da' : 'ne'}
									</li>
									<li>
										<span style={{ color: 'gray' }}>Vrača akumulator:</span> {inst!.metadata.returnsAccumulator ? 'da' : 'ne'}
									</li>
									<li><span style={{ color: 'gray' }}>Argumenti:</span> {inst!.metadata.arguments.length}</li>
									{inst!.metadata.arguments.map((variant: any[], i: number) => (
										<li key={i}>
											<span style={{ color: 'gray' }}>&nbsp;&nbsp;{i + 1}.</span>
											<ul style={{ listStyleType: 'none' }}>
												{variant.map((arg, j, array) => (
													<li key={j}>
														<ul style={{ listStyleType: 'none' }}>
															<li><span style={{ color: 'gray' }}>Ime: </span>{arg.argName}</li>
															<li><span style={{ color: 'gray' }}>Tip: </span>{arg.argToken}</li>
															<li><span style={{ color: 'gray' }}>Opis: </span>{arg.argDescription}</li>
														</ul>
														{j + 1 < array.length ? <div style={{ margin: 10, color: '#bbbbbbff' }}>&nbsp;ali</div> : <></>}
													</li>
												))}
											</ul>
										</li>
									))}

									<li><span style={{ color: 'gray' }}>Primer zapisa:</span> {inst!.metadata.example}</li>
								</ul>
							</li>
						))}
					</ul>
				</li>
				<li style={{ fontSize: 10 }}>
					🎅 Ooo, prelistal si do konca dokumentacije? No, verjetno si samo prescrollal, ampak vseeno - Božiček je dodal poseben plus zraven tvojega imena na svojem seznamu pridnih!
				</li>
			</ul>
		</div>
	);
};

export default ElfDocs;
